import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ProjectKanbanCard } from './ProjectKanbanCard';
import type { PipelineStatus } from '@/hooks/usePipelines';
import type { Project } from '@/hooks/useProjects';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';

interface ProjectKanbanColumnProps {
  status: PipelineStatus;
  projects: Project[];
  onAddProject?: (statusId: string) => void;
}

export function ProjectKanbanColumn({ status, projects, onAddProject }: ProjectKanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: status.id });

  return (
    <div className="flex flex-col w-72 flex-shrink-0">
      {/* Column header */}
      <div className="flex items-center gap-2 mb-3 px-1">
        <div
          className="h-3 w-3 rounded-full flex-shrink-0"
          style={{ backgroundColor: status.color }}
        />
        <span className="text-sm font-semibold truncate flex-1">{status.name}</span>
        <Badge variant="secondary" className="text-xs h-5">
          {projects.length}
        </Badge>
      </div>

      {/* Drop zone */}
      <div
        ref={setNodeRef}
        className={`flex-1 min-h-32 rounded-lg border-2 border-dashed p-2 space-y-2 transition-colors ${
          isOver ? 'border-primary/60 bg-primary/5' : 'border-border/50 bg-muted/20'
        }`}
      >
        <SortableContext
          items={projects.map(p => p.id)}
          strategy={verticalListSortingStrategy}
        >
          {projects.map(project => (
            <ProjectKanbanCard key={project.id} project={project} />
          ))}
        </SortableContext>

        {projects.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-6">Drop here</p>
        )}
      </div>

      {/* Add project button */}
      {onAddProject && (
        <Button
          variant="ghost"
          size="sm"
          className="mt-2 w-full text-muted-foreground hover:text-foreground justify-start gap-1"
          onClick={() => onAddProject(status.id)}
        >
          <Plus className="h-4 w-4" />
          Add project
        </Button>
      )}
    </div>
  );
}
